import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  Award,
  Users,
  Link as LinkIcon,
  Copy,
  Plus,
  Calendar,
  ChevronRight,
  ChevronLeft,
  CheckCircle,
  Clock,
  TrendingUp,
  Sparkles,
  X,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useAuthStore } from '@/lib/store';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

interface InviteCode {
  id: string;
  code: string;
  max_uses: number;
  current_uses: number;
  is_active: boolean;
  expires_at: string | null;
  created_at: string;
}

interface Referral {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  created_at: string;
}

interface AmbassadorStats {
  total_invite_codes: number;
  total_signups: number;
  active_codes: number;
}

export default function AmbassadorPortal() {
  const { user } = useAuthStore();
  const [stats, setStats] = useState<AmbassadorStats | null>(null);
  const [codes, setCodes] = useState<InviteCode[]>([]);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newMaxUses, setNewMaxUses] = useState<number>(10);
  const [newExpiresAt, setNewExpiresAt] = useState('');
  const [codePage, setCodePage] = useState(1);
  const [referralPage, setReferralPage] = useState(1);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const [statsData, codesData, referralsData] = await Promise.all([
          api.getAmbassadorStats(),
          api.getInviteCodes({ page: codePage }),
          api.getReferrals({ page: referralPage }),
        ]);
        if (!mounted) return;
        setStats(statsData);
        setCodes(codesData.data ?? []);
        setReferrals(referralsData.data ?? []);
      } catch {
        // silently fail
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [codePage, referralPage]);

  const handleCreateCode = async () => {
    setCreating(true);
    try {
      const result = await api.createInviteCode({
        max_uses: newMaxUses,
        expires_at: newExpiresAt ? new Date(newExpiresAt).toISOString() : undefined,
      });
      setCodes((prev) => [result, ...prev]);
      setShowCreateForm(false);
      setNewMaxUses(10);
      setNewExpiresAt('');
      toast.success('Invite code created!');
    } catch (err: any) {
      toast.error(err.message || 'Failed to create code');
    } finally {
      setCreating(false);
    }
  };

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-[3px] border-gray-200 dark:border-gray-800 border-t-accent-500 rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Loading ambassador portal...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div initial="hidden" animate="visible" className="space-y-6">
      {/* Header */}
      <motion.div variants={fadeUp} custom={0}>
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent-500 to-brand-500 flex items-center justify-center shadow-lg shadow-accent-500/20">
            <Award className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="page-header">Ambassador Portal</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Invite students and grow the APEX community
            </p>
          </div>
        </div>
      </motion.div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <motion.div variants={fadeUp} custom={1} className="stat-card">
          <div className="w-8 h-8 rounded-lg bg-accent-50 dark:bg-accent-900/20 flex items-center justify-center mb-1">
            <LinkIcon className="w-4 h-4 text-accent-500" />
          </div>
          <span className="text-2xl font-bold">{stats?.total_invite_codes ?? 0}</span>
          <span className="text-xs text-gray-500 dark:text-gray-400">Total codes</span>
        </motion.div>

        <motion.div variants={fadeUp} custom={2} className="stat-card">
          <div className="w-8 h-8 rounded-lg bg-success-50 dark:bg-green-900/20 flex items-center justify-center mb-1">
            <CheckCircle className="w-4 h-4 text-success-500" />
          </div>
          <span className="text-2xl font-bold">{stats?.active_codes ?? 0}</span>
          <span className="text-xs text-gray-500 dark:text-gray-400">Active codes</span>
        </motion.div>

        <motion.div variants={fadeUp} custom={3} className="stat-card">
          <div className="w-8 h-8 rounded-lg bg-brand-50 dark:bg-brand-900/20 flex items-center justify-center mb-1">
            <Users className="w-4 h-4 text-brand-500" />
          </div>
          <span className="text-2xl font-bold">{stats?.total_signups ?? 0}</span>
          <span className="text-xs text-gray-500 dark:text-gray-400">Total signups</span>
        </motion.div>
      </div>

      {/* Invite Codes */}
      <motion.div variants={fadeUp} custom={4} className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="section-header">Invite Codes</h2>
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="btn-primary text-sm"
          >
            {showCreateForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showCreateForm ? 'Cancel' : 'New Code'}
          </button>
        </div>

        {/* Create form */}
        {showCreateForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-6 p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Max Uses</label>
                <input
                  type="number"
                  min={1}
                  value={newMaxUses}
                  onChange={(e) => setNewMaxUses(parseInt(e.target.value) || 10)}
                  className="input-field"
                />
              </div>
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Expires At (optional)</label>
                <input
                  type="date"
                  value={newExpiresAt}
                  onChange={(e) => setNewExpiresAt(e.target.value)}
                  className="input-field"
                />
              </div>
            </div>
            <button onClick={handleCreateCode} disabled={creating} className="btn-primary">
              {creating ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Creating...
                </div>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Generate Code
                </>
              )}
            </button>
          </motion.div>
        )}

        {/* Code list */}
        {codes.length === 0 ? (
          <div className="text-center py-8">
            <LinkIcon className="w-10 h-10 text-gray-300 dark:text-gray-700 mx-auto mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400">No invite codes yet</p>
            <p className="text-xs text-gray-400 mt-1">Create your first invite code to start referring students</p>
          </div>
        ) : (
          <div className="space-y-3">
            {codes.map((code) => (
              <div
                key={code.id}
                className={`flex items-center gap-4 p-4 rounded-xl border ${
                  code.is_active
                    ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                    : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 opacity-60'
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <code className="text-base font-mono font-bold tracking-wider text-brand-600 dark:text-brand-400">
                      {code.code}
                    </code>
                    <button
                      onClick={() => copyCode(code.code)}
                      className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      title="Copy code"
                    >
                      <Copy className="w-3.5 h-3.5 text-gray-400" />
                    </button>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 dark:text-gray-400">
                    <span>{code.current_uses}/{code.max_uses} used</span>
                    {code.expires_at && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Expires {new Date(code.expires_at).toLocaleDateString()}
                      </span>
                    )}
                    <span className={code.is_active ? 'text-success-500' : 'text-gray-400'}>
                      {code.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>

                {/* Usage bar */}
                <div className="hidden sm:block w-24">
                  <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-accent-500 rounded-full"
                      style={{ width: `${(code.current_uses / Math.max(code.max_uses, 1)) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.div>

      {/* Recent Referrals */}
      <motion.div variants={fadeUp} custom={5} className="card p-6">
        <h2 className="section-header mb-4">Recent Referrals</h2>

        {referrals.length === 0 ? (
          <div className="text-center py-8">
            <Users className="w-10 h-10 text-gray-300 dark:text-gray-700 mx-auto mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400">No referrals yet</p>
            <p className="text-xs text-gray-400 mt-1">Share your invite codes with students to start building your network</p>
          </div>
        ) : (
          <div className="space-y-3">
            {referrals.map((ref) => (
              <div key={ref.id} className="flex items-center gap-4 p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-500 to-accent-500 flex items-center justify-center text-white font-semibold text-sm">
                  {ref.first_name?.[0]}{ref.last_name?.[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold">{ref.first_name} {ref.last_name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{ref.email}</p>
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {new Date(ref.created_at).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}
